import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const enrollCourses = [
  {
    value: "1",
    label: "Executive Architect",
    tagline: "Design at the scale of cities.",
    duration: "6 months",
    href: "/course-executive-architect#admissions",
  },
  {
    value: "2",
    label: "Executive Interior Designer",
    tagline: "Shape how people live inside space.",
    duration: "5 months",
    href: "/course-executive-interior#admissions",
  },
  {
    value: "3",
    label: "Custom Program",
    tagline: "Built around what you already know.",
    duration: "Flexible",
    href: "/customise-your-program#customize",
  },
];

function EnrollPage() {
  const [selected, setSelected] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Enroll - IPDI";
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStart = () => {
    if (selected) {
      navigate(selected.href);
    }
  };

  return (
    <main className="public-page architect-theme enroll-page">
      <section className="enroll-split">
        <div className="enroll-left">
          <div className="course-shell enroll-shell ipdi-enroll-shell">
            <div className="ipdi-enroll-eyebrow">
              <span className="ipdi-enroll-pulse" aria-hidden="true">
                <span className="ipdi-enroll-pulse-core" />
                <span className="ipdi-enroll-pulse-ring" />
              </span>
              Admissions open
            </div>

            <h1>Enroll</h1>
            <p className="ipdi-enroll-subhead">
              Pick a program, see what it&rsquo;s about, and start your application below.
            </p>

            <div className="ipdi-enroll-field" ref={wrapRef}>
              <span className="ipdi-enroll-field-label">Course</span>

              <button
                type="button"
                className={`ipdi-enroll-trigger ${isOpen ? "is-open" : ""}`}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
              >
                <span className={selected ? "" : "is-placeholder"}>
                  {selected ? selected.label : "Select course"}
                </span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true" className={`ipdi-enroll-trigger-icon ${isOpen ? "is-open" : ""}`}>
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <div className={`ipdi-enroll-menu ${isOpen ? "is-open" : ""}`}>
                {enrollCourses.map((course) => (
                  <button
                    type="button"
                    key={course.value}
                    className={`ipdi-enroll-option ${selected?.value === course.value ? "is-selected" : ""}`}
                    onClick={() => {
                      setSelected(course);
                      setIsOpen(false);
                    }}
                  >
                    <span className="ipdi-enroll-option-main">
                      <span className="ipdi-enroll-option-label">{course.label}</span>
                      <span className="ipdi-enroll-option-tagline">{course.tagline}</span>
                    </span>
                    <span className="ipdi-enroll-option-duration">{course.duration}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="course-primary-button enroll-button ipdi-enroll-button"
              onClick={handleStart}
              disabled={!selected}
            >
              <span>Start application</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <div className="ipdi-enroll-stats">
              <span>3 programs</span>
              <span className="ipdi-enroll-stats-dot" aria-hidden="true" />
              <span>Rolling admissions</span>
            </div>
          </div>
        </div>

        <div className="enroll-right">
          <div className={`ipdi-enroll-card ${selected ? "has-selection" : ""}`}>
            <span className="ipdi-enroll-card-eyebrow">
              {selected ? selected.duration : "Institute of Planning, Design and Innovation"}
            </span>
            <span className="ipdi-enroll-card-title">
              {selected ? selected.label : "Choose a program to preview it here"}
            </span>
          </div>
        </div>
      </section>

      <style>{`
        :root {
          --enroll-inset: 2.5rem; /* match header wrapper padding (0 2.5rem) */
        }

        .enroll-split .enroll-left {
          padding-left: var(--enroll-inset);
          padding-right: 32px;
          box-sizing: border-box;
        }

        .enroll-split .enroll-right {
          position: relative;
          overflow: hidden;
        }

        .ipdi-enroll-shell {
          max-width: 440px;
        }

        .ipdi-enroll-eyebrow {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 12px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #C77B4D;
          margin-bottom: 20px;
        }

        .ipdi-enroll-pulse {
          position: relative;
          width: 8px;
          height: 8px;
          display: inline-flex;
        }

        .ipdi-enroll-pulse-core {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: #C77B4D;
        }

        .ipdi-enroll-pulse-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: #C77B4D;
          animation: ipdi-pulse 2.2s ease-out infinite;
        }

        @keyframes ipdi-pulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(2.6); opacity: 0; }
        }

        .ipdi-enroll-shell h1 {
          margin-bottom: 10px;
          letter-spacing: -0.01em;
        }

        .ipdi-enroll-subhead {
          margin-bottom: 36px;
          max-width: 340px;
          color: rgba(245, 241, 234, 0.65);
          font-size: 15px;
          line-height: 1.6;
        }

        .ipdi-enroll-field {
          position: relative;
          margin-bottom: 20px;
        }

        .ipdi-enroll-field-label {
          display: block;
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(245, 241, 234, 0.4);
          margin-bottom: 10px;
        }

        .ipdi-enroll-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(245, 241, 234, 0.04);
          border: 1px solid rgba(245, 241, 234, 0.16);
          color: #F5F1EA;
          font-size: 15px;
          padding: 15px 18px;
          border-radius: 2px;
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .ipdi-enroll-trigger:hover {
          border-color: rgba(245, 241, 234, 0.32);
        }

        .ipdi-enroll-trigger.is-open {
          border-color: #C77B4D;
          background: rgba(245, 241, 234, 0.06);
        }

        .ipdi-enroll-trigger .is-placeholder {
          color: rgba(245, 241, 234, 0.4);
        }

        .ipdi-enroll-trigger-icon {
          color: rgba(245, 241, 234, 0.5);
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }

        .ipdi-enroll-trigger-icon.is-open {
          transform: rotate(180deg);
          color: #C77B4D;
        }

        .ipdi-enroll-menu {
          position: absolute;
          left: 0;
          right: 0;
          top: calc(100% + 8px);
          background: #171513;
          border: 1px solid rgba(245, 241, 234, 0.14);
          border-radius: 2px;
          z-index: 10;
          overflow: hidden;
          max-height: 0;
          opacity: 0;
          transform: translateY(-6px);
          transition: max-height 0.22s ease, opacity 0.18s ease, transform 0.18s ease;
          pointer-events: none;
        }

        .ipdi-enroll-menu.is-open {
          max-height: 260px;
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .ipdi-enroll-option {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: none;
          border: none;
          border-bottom: 1px solid rgba(245, 241, 234, 0.08);
          color: #F5F1EA;
          text-align: left;
          padding: 14px 18px;
          cursor: pointer;
          transition: background 0.15s ease;
        }

        .ipdi-enroll-option:last-child {
          border-bottom: none;
        }

        .ipdi-enroll-option:hover,
        .ipdi-enroll-option.is-selected {
          background: rgba(199, 123, 77, 0.1);
        }

        .ipdi-enroll-option-main {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .ipdi-enroll-option-label {
          font-size: 14px;
        }

        .ipdi-enroll-option-tagline {
          font-size: 12px;
          font-style: italic;
          color: rgba(245, 241, 234, 0.5);
        }

        .ipdi-enroll-option-duration {
          font-size: 12px;
          color: rgba(245, 241, 234, 0.4);
          flex-shrink: 0;
        }

        .ipdi-enroll-button {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-top: 8px;
          transition: transform 0.15s ease, opacity 0.15s ease;
        }

        .ipdi-enroll-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .ipdi-enroll-button:not(:disabled):hover {
          transform: translateX(2px);
        }

        .ipdi-enroll-stats {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 24px;
          font-size: 12px;
          color: rgba(245, 241, 234, 0.4);
        }

        .ipdi-enroll-stats-dot {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: rgba(245, 241, 234, 0.4);
        }

        .ipdi-enroll-card {
          position: absolute;
          left: 28px;
          right: 28px;
          bottom: 28px;
          z-index: 2;
          padding: 20px 22px;
          background: rgba(14, 13, 12, 0.55);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(245, 241, 234, 0.16);
          border-radius: 4px;
          transition: border-color 0.25s ease, background 0.25s ease;
        }

        .ipdi-enroll-card.has-selection {
          border-color: rgba(199, 123, 77, 0.5);
          background: rgba(14, 13, 12, 0.68);
        }

        .ipdi-enroll-card-eyebrow {
          display: block;
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(233, 168, 118, 0.85);
          margin-bottom: 8px;
        }

        .ipdi-enroll-card-title {
          display: block;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 19px;
          line-height: 1.3;
          color: #F5F1EA;
        }

        @media (max-width: 768px) {

  :root{
    --enroll-inset: 1rem;   /* 16px */
  }

  .enroll-split .enroll-left{
    padding-left: var(--enroll-inset);
    padding-right: var(--enroll-inset);
  }

  .ipdi-enroll-shell{
    max-width:100%;
  }

}

        @media (max-width: 900px) {
          :root { --enroll-inset: 2.5rem; }
          .enroll-split .enroll-left {
            padding-right: 24px;
          }
          .ipdi-enroll-card {
            left: 20px;
            right: 20px;
            bottom: 20px;
          }
        }

@media (max-width:768px){

  .enroll-split .enroll-left{
    padding-left:16px !important;
    padding-right:16px !important;
  }

  .ipdi-enroll-shell{
    max-width:100%;
    width:100%;
  }

}

        /* Large-screen full-bleed background + max-width contained content,
           matching the home page pattern (same 1600px breakpoint, same
           1400px cap, same calc-padding technique used for .dfrp/.programs/
           etc. on the home page). The black panel and the image panel stay
           edge-to-edge; only the inner content (shell + card) aligns to
           where a centered 1400px container's edges would fall. Desktop,
           tablet, and mobile breakpoints above are untouched. */
        @media (min-width: 1600px) {
          .enroll-split .enroll-left {
            padding-left: calc((100vw - 1400px) / 2 + var(--enroll-inset));
          }

          .ipdi-enroll-card {
            right: calc((100vw - 1400px) / 2 + 28px);
          }
        }

      `}

      
      
      </style>
    </main>
  );
}

export default EnrollPage;
